// AreaSearch.js
import {
    Typography, Select, MenuItem, Stack, Chip,
    AccordionDetails, Box, ButtonBase
  } from "@mui/material";
  import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
  import TuneIcon from "@mui/icons-material/Tune";
  import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
  import QuestionFilterComponent from "./QuestionFilterComponent";
  
  // ─── Design tokens (matches Sidebar light theme) ──────────────────────────────
  const T = {
    bg: "#ffffff",
    surface: "#f8f9fc",
    surfaceHover: "#f0f2f7",
    border: "rgba(0,0,0,0.07)",
    borderStrong: "rgba(0,0,0,0.13)",
    text: "#1a1d23",
    textMuted: "#6b7280",
    textFaint: "#9ca3af",
    accent: "#2563eb",
    accentDim: "rgba(37,99,235,0.08)",
    accentGlow: "rgba(37,99,235,0.04)",
    chip: "#f3f4f6",
    chipActive: "rgba(37,99,235,0.1)",
    chipActiveBorder: "rgba(37,99,235,0.35)",
  };
  
  const SectionLabel = ({ children }) => (
    <Typography sx={{
      fontSize: "10px", fontWeight: 700, color: T.textFaint,
      letterSpacing: "0.1em", textTransform: "uppercase",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {children}
    </Typography>
  );
  
  const AreaSearch = ({
    groupsById,
    detailsOpen,
    ActionTargetVariables,
    onActionTargetVariableChange,
    ActionTargetSubVariables,
    handleActionTargetSubVariableChange,
    ActionMode,
    ActionTargetConcentration,
    handleActionTargetConcentrationChange,
    selectedDemographics,
  }) => {
    const selectedVar1 = ActionTargetVariables.TargetVariable1
      ? groupsById?.[ActionTargetVariables.TargetVariable1]
      : null;
  
    const selectSx = {
      borderRadius: "8px",
      bgcolor: T.chip,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: T.border,
        transition: "border-color 0.15s",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: T.borderStrong },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: T.accent },
      "& .MuiSelect-select": {
        color: T.text,
        fontSize: "13px",
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: 500,
        textAlign: "left !important",
        display: "flex !important",
        alignItems: "center !important",
        py: 1.1,
      },
      "& .MuiSvgIcon-root": { color: T.textFaint },
    };
  
    const menuPropsSx = {
      PaperProps: {
        sx: {
          bgcolor: T.bg,
          border: `1px solid ${T.borderStrong}`,
          borderRadius: "10px",
          mt: 0.5,
          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
          "& .MuiMenuItem-root": {
            fontSize: "13px",
            color: T.textMuted,
            fontFamily: "'DM Sans', sans-serif",
            py: 0.75,
            px: 2,
            borderRadius: "6px",
            mx: 0.5,
            "&:hover": { bgcolor: T.surface, color: T.text },
            "&.Mui-selected": { bgcolor: T.accentDim, color: T.accent, fontWeight: 600 },
          },
        },
      },
    };
  
    return (
      <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.25, fontFamily: "'DM Sans', sans-serif" }}>
  
        {/* Variable Selector */}
        <Box sx={{ mb: 2 }}>
          <SectionLabel>Select Variable</SectionLabel>
          <Box sx={{ mt: 1 }}>
            <Select
              value={ActionTargetVariables.TargetVariable1 ?? ""}
              onChange={(e) => onActionTargetVariableChange?.({ val: e.target.value, varId: "1" })}
              displayEmpty
              size="small"
              fullWidth
              MenuProps={menuPropsSx}
              sx={selectSx}
            >
              {(Object.keys(groupsById) || []).map((variable) => (
                <MenuItem key={variable} value={variable}>
                  {groupsById[variable]?.label || variable}
                </MenuItem>
              ))}
            </Select>
  
            {selectedVar1?.fulltext && (
              <Box sx={{ mt: 1.25 }}>
                <Stack direction="row" alignItems="flex-start" spacing={0.75}>
                  <InfoOutlinedIcon sx={{ fontSize: 13, color: T.textFaint, mt: "2px", flexShrink: 0 }} />
                  <Typography sx={{
                    fontSize: "12px", color: T.textFaint, lineHeight: 1.6,
                    fontFamily: "'DM Sans', sans-serif",
                  }}>
                    {selectedVar1.fulltext}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
  
        {/* Target Response Picker */}
        <Box sx={{ mb: 2 }}>
          <SectionLabel>Pick the Response to Target</SectionLabel>
          <Box sx={{ mt: 1 }}>
            <QuestionFilterComponent
              groupsById={groupsById}
              selectedVariable={ActionTargetVariables.TargetVariable1}
              preferenceFilter={ActionTargetSubVariables.TargetSubVariable1}
              onPreferenceFilterChange={(val) => handleActionTargetSubVariableChange({ val, varId: "1" })}
              ActionMode={ActionMode}
            />
          </Box>
        </Box>
  
        {/* Concentration Selector */}
        <Box sx={{ mb: selectedDemographics && Object.values(selectedDemographics).some(Boolean) ? 2 : 0 }}>
          <SectionLabel>Show Areas Where Response Is Most Concentrated</SectionLabel>
          <Box sx={{ mt: 1 }}>
            <Select
              value={ActionTargetConcentration ?? "all"}
              onChange={(e) => handleActionTargetConcentrationChange?.(e.target.value)}
              size="small"
              fullWidth
              MenuProps={menuPropsSx}
              sx={selectSx}
            >
              <MenuItem value="all">All areas</MenuItem>
              <MenuItem value="top50">Top 50%</MenuItem>
              <MenuItem value="top25">Top 25%</MenuItem>
              <MenuItem value="top10">Top 10%</MenuItem>
            </Select>
            <Typography sx={{
              fontSize: "11px", color: T.textFaint, mt: 0.75,
              fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5,
            }}>
              Highlights the strongest concentrations for the selected response.
            </Typography>
          </Box>
        </Box>
  
        {/* Active Demographic Filters */}
        {selectedDemographics && Object.values(selectedDemographics).some(Boolean) && (
          <Box>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
              <FilterAltOutlinedIcon sx={{ fontSize: 12, color: T.textFaint }} />
              <SectionLabel>Active Demographic Filters</SectionLabel>
            </Stack>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {Object.entries(selectedDemographics).map(([key, label]) =>
                label ? (
                  <Box
                    key={key}
                    sx={{
                      px: 1, py: 0.3, borderRadius: "5px",
                      bgcolor: T.accentDim, border: `1px solid ${T.chipActiveBorder}`,
                    }}
                  >
                    <Typography sx={{
                      fontSize: "11px", color: T.accent,
                      fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
                    }}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}: {label}
                    </Typography>
                  </Box>
                ) : null
              )}
            </Box>
          </Box>
        )}
      </AccordionDetails>
    );
  };
  
  export default AreaSearch;